/**
 * React hook for Service Worker integration
 */

import { useState, useEffect, useCallback } from 'react';
import { serviceWorkerManager, ServiceWorkerConfig, CacheStats } from '../services/ServiceWorkerManager';

export interface ServiceWorkerState {
  isSupported: boolean;
  isActive: boolean;
  isUpdateAvailable: boolean;
  isInstalling: boolean;
  cacheStats: CacheStats;
  error: string | null;
}

export interface ServiceWorkerActions {
  skipWaiting: () => Promise<void>;
  checkForUpdates: () => Promise<void>;
  clearCache: (cacheName: string) => Promise<void>;
  preloadResources: (resources: Array<{ url: string; type: string }>) => Promise<void>;
  queueOfflineAction: (action: any) => Promise<void>;
  updateConfig: (config: Partial<ServiceWorkerConfig>) => void;
  refreshCacheStats: () => Promise<void>;
}

export function useServiceWorker(): [ServiceWorkerState, ServiceWorkerActions] {
  const [state, setState] = useState<ServiceWorkerState>({
    isSupported: 'serviceWorker' in navigator,
    isActive: false,
    isUpdateAvailable: false,
    isInstalling: false,
    cacheStats: {},
    error: null,
  });

  // Initialize service worker
  useEffect(() => {
    const initializeServiceWorker = async () => {
      try {
        await serviceWorkerManager.initialize();
        
        setState(prev => ({
          ...prev,
          isActive: serviceWorkerManager.isActive(),
          error: null,
        }));

        // Load initial cache stats
        const stats = await serviceWorkerManager.getCacheStats();
        setState(prev => ({
          ...prev,
          cacheStats: stats,
        }));

      } catch (error) {
        setState(prev => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Service Worker initialization failed',
        }));
      }
    };

    if (state.isSupported) {
      initializeServiceWorker();
    }
  }, [state.isSupported]);

  // Listen for service worker events
  useEffect(() => {
    const handleUpdateAvailable = (_event: CustomEvent) => {
      setState(prev => ({
        ...prev,
        isUpdateAvailable: true,
      }));
    };

    const handleInstalling = () => {
      setState(prev => ({
        ...prev,
        isInstalling: true,
      }));
    };

    const handleInstalled = () => {
      setState(prev => ({
        ...prev,
        isInstalling: false,
      }));
    };

    // Listen for custom events
    window.addEventListener('sw-update-available', handleUpdateAvailable as EventListener);
    
    // Listen for service worker state changes
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        const { type } = event.data;
        
        switch (type) {
          case 'INSTALLING':
            handleInstalling();
            break;
          case 'INSTALLED':
            handleInstalled();
            break;
        }
      });
    }

    return () => {
      window.removeEventListener('sw-update-available', handleUpdateAvailable as EventListener);
    };
  }, []);

  // Actions
  const skipWaiting = useCallback(async () => {
    try {
      await serviceWorkerManager.skipWaiting();
      setState(prev => ({
        ...prev,
        isUpdateAvailable: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to skip waiting',
      }));
    }
  }, []);

  const checkForUpdates = useCallback(async () => {
    try {
      await serviceWorkerManager.checkForUpdates();
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to check for updates',
      }));
    }
  }, []);

  const clearCache = useCallback(async (cacheName: string) => {
    try {
      await serviceWorkerManager.clearCache(cacheName);
      
      // Refresh cache stats
      const stats = await serviceWorkerManager.getCacheStats();
      setState(prev => ({
        ...prev,
        cacheStats: stats,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to clear cache',
      }));
    }
  }, []);

  const preloadResources = useCallback(async (resources: Array<{ url: string; type: string }>) => {
    try {
      await serviceWorkerManager.preloadResources(resources);
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to preload resources',
      }));
    }
  }, []);

  const queueOfflineAction = useCallback(async (action: any) => {
    try {
      await serviceWorkerManager.queueOfflineAction(action);
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to queue offline action',
      }));
    }
  }, []);

  const updateConfig = useCallback((config: Partial<ServiceWorkerConfig>) => {
    serviceWorkerManager.updateConfig(config);
  }, []);

  const refreshCacheStats = useCallback(async () => {
    try {
      const stats = await serviceWorkerManager.getCacheStats();
      setState(prev => ({
        ...prev,
        cacheStats: stats,
        error: null,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to refresh cache stats',
      }));
    }
  }, []);

  const actions: ServiceWorkerActions = {
    skipWaiting,
    checkForUpdates,
    clearCache,
    preloadResources,
    queueOfflineAction,
    updateConfig,
    refreshCacheStats,
  };

  return [state, actions];
}

/**
 * Hook for offline status detection
 */
export function useOfflineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        // Trigger background sync when coming back online
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.ready.then(registration => {
            if ('sync' in registration) {
              (registration as any).sync.register('background-sync');
            }
          });
        }
        setWasOffline(false);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  return {
    isOnline,
    isOffline: !isOnline,
    wasOffline,
  };
}

/**
 * Hook for service worker update notifications
 */
export function useServiceWorkerUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [skipWaitingFn, setSkipWaitingFn] = useState<(() => void) | null>(null);

  useEffect(() => {
    const handleUpdateAvailable = (event: CustomEvent) => {
      setUpdateAvailable(true);
      setSkipWaitingFn(() => event.detail.skipWaiting);
    };

    window.addEventListener('sw-update-available', handleUpdateAvailable as EventListener);

    return () => {
      window.removeEventListener('sw-update-available', handleUpdateAvailable as EventListener);
    };
  }, []);

  const applyUpdate = useCallback(() => {
    if (skipWaitingFn) {
      skipWaitingFn();
      setUpdateAvailable(false);
      setSkipWaitingFn(null);
    }
  }, [skipWaitingFn]);

  const dismissUpdate = useCallback(() => {
    setUpdateAvailable(false);
    setSkipWaitingFn(null);
  }, []);

  return {
    updateAvailable,
    applyUpdate,
    dismissUpdate,
  };
}